import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/app/admin/_common/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/app/admin/_common/ui/sheet";
import CuoButton from "@/app/admin/_common/CuoButton";
import { InviteUserForm } from "./InviteUserForm";
import { useInviteUserFlow } from "./useInviteUserFlow";
import type { AdminUser, Role } from "@/app/_services/RbacService";
import { UserPlus, X } from "lucide-react";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";

const INVITE_DESCRIPTION =
  "They will get an email inviting them to set a password or continue with Google.";
const EDIT_DESCRIPTION = "Change which roles this person holds.";

export const InviteUserDialog = ({
  open,
  editingUser,
  roles,
  onClose,
  onSaved,
}: {
  open: boolean;
  editingUser?: AdminUser | null;
  roles: Role[];
  onClose: () => void;
  onSaved: () => void;
}) => {
  const { control, errors, isSaving, handleSave } = useInviteUserFlow({
    editingUser,
    onSaved: () => {
      onSaved();
      onClose();
    },
  });
  const isMobile = useIsMobile();

  const isEditing = Boolean(editingUser);
  const title = isEditing ? editingUser!.email : "Invite user";
  const description = isEditing ? EDIT_DESCRIPTION : INVITE_DESCRIPTION;

  const form = (
    <InviteUserForm
      control={control}
      errors={errors}
      roles={roles}
      isEditing={isEditing}
    />
  );

  const footerButtons = (
    <>
      <CuoButton variant="outline" label="Cancel" onClick={onClose} />
      <CuoButton
        disableOnAsync={isSaving}
        variant="default"
        onClick={handleSave}
        label={isEditing ? "Save" : "Send invitation"}
      />
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
        <SheetContent
          side="right"
          hideClose
          className="flex h-full w-screen max-w-none flex-col gap-0 border-l-0 bg-white p-0 sm:max-w-none"
        >
          <SheetTitle className="sr-only">{title}</SheetTitle>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <span className="inline-flex items-center gap-2 text-[13px] font-normal text-foreground">
              <UserPlus className="size-4 flex-none text-muted-foreground" />
              {title}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
              aria-label="Close"
            >
              <X className="size-3.5" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
            <p className="text-[12.5px] text-muted-foreground">{description}</p>
            {form}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-border p-3">
            {footerButtons}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="inset-0 left-0 top-0 m-auto h-fit max-h-[85vh] w-full max-w-[440px] translate-x-0 translate-y-0 bg-white">
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2 align-middle">
              <UserPlus className="size-4 flex-none text-muted-foreground" />
              {title}
            </span>
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto py-2">{form}</div>

        <DialogFooter>{footerButtons}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
